import { type CSSProperties, useEffect, useRef, useState } from "react";
import defaultSite from "./content/site.json";
import { upgradeSiteContent } from "./content/upgrade";
import { serializeStructuredData } from "./seo";
import type { InstagramPost } from "../server/instagram.mjs";
import CmsApp from "./Cms";
import { useCarousel } from "./useCarousel";

type Language = "zh" | "en";
type Localized = { zh: string; en: string };
type SiteContent = typeof defaultSite;

const copy = (value: Localized, language: Language) => value[language];

const carouselPositionClass = (position: number) => {
  if (position === 0) return "is-center";
  if (position === -1) return "is-prev";
  if (position === 1) return "is-next";
  if (position === -2) return "is-prev-outer";
  if (position === 2) return "is-next-outer";
  return position < 0 ? "is-before" : "is-after";
};

function BrandMark({ site, tone = "light" }: { site: SiteContent; tone?: "light" | "dark" }) {
  return (
    <span className={`brand-mark brand-mark--${tone}`} aria-hidden="true">
      <img src={site.brand.logo} alt="" width="400" height="400" />
    </span>
  );
}

function IntroOverlay({ site, leaving }: { site: SiteContent; leaving: boolean }) {
  return (
    <div className={`intro-overlay ${leaving ? "is-leaving" : ""}`} aria-hidden="true">
      <div className="intro-overlay__curtain" />
      <img className="intro-overlay__logo" src={site.brand.logo} alt="" />
    </div>
  );
}

function Header({ site, language, onLanguage }: { site: SiteContent; language: Language; onLanguage: () => void }) {
  const [open, setOpen] = useState(false);
  const navigationRef = useRef<HTMLElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    document.body.classList.toggle("menu-open", open);
    return () => document.body.classList.remove("menu-open");
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const getFocusable = () => Array.from(
      navigationRef.current?.querySelectorAll<HTMLElement>('a[href], button:not([disabled])') ?? [],
    );
    const focusTimer = window.setTimeout(() => getFocusable()[0]?.focus(), 180);
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setOpen(false);
        window.setTimeout(() => triggerRef.current?.focus(), 0);
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = getFocusable();
      const first = focusable[0];
      const last = focusable.at(-1);
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKey);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const closeMenu = () => setOpen(false);

  return (
    <>
      <header className="site-header">
        <button
          ref={triggerRef}
          className="menu-trigger"
          type="button"
          aria-label={language === "zh" ? (open ? "關閉導覽" : "開啟導覽") : (open ? "Close navigation" : "Open navigation")}
          aria-expanded={open}
          aria-controls="site-navigation"
          onClick={() => setOpen((value) => !value)}
        >
          <span className="menu-trigger__lines" aria-hidden="true"><i /><i /></span>
        </button>
        <a className="header-logo" href="#top" aria-label={copy(site.brand.ariaLabel, language)}>
          <BrandMark site={site} />
        </a>
        <button
          className="language-toggle"
          type="button"
          onClick={onLanguage}
          aria-describedby="language-toggle-description"
        >
          <span className={language === "zh" ? "is-active" : ""}>中</span>
          <span className={language === "en" ? "is-active" : ""}>EN</span>
          <span id="language-toggle-description" className="sr-only">{copy(site.interface.switchLanguage, language)}</span>
        </button>
      </header>

      <nav
        ref={navigationRef}
        id="site-navigation"
        className={`site-navigation ${open ? "is-open" : ""}`}
        aria-label={copy(site.interface.mobileNavigation, language)}
        aria-hidden={!open}
        inert={!open}
      >
        <div className="site-navigation__inner">
          <div className="site-navigation__links">
            {site.navigation.map((item) => (
              <a key={item.id} href={`#${item.id}`} onClick={closeMenu}>
                {copy(item.label, language)}
              </a>
            ))}
          </div>
          <div className="site-navigation__social">
            {site.contact.social.map((item) => (
              <a key={item.label} href={item.href} className="social-link"
                target="_blank" rel="noopener noreferrer"
                aria-label={`${item.label} (${language === "zh" ? "新視窗" : "new window"})`}>
                <SocialIcon label={item.label} />
              </a>
            ))}
          </div>
        </div>
      </nav>
    </>
  );
}

function Hero({ site, language }: { site: SiteContent; language: Language }) {
  return (
    <section id="top" className="hero" aria-labelledby="hero-title">
      <img className="hero__image" src={site.hero.image} alt={copy(site.hero.alt, language)} width="1536" height="1024" fetchPriority="high" />
      <div className="hero__shade" />
      <div className="hero__content page-shell">
        <div className="hero__copy">
          <h1 id="hero-title">
            {copy(site.hero.title, language)}
          </h1>
          <p>{copy(site.hero.subtitle, language)}</p>
          <div className="hero__actions">
            <a className="button button--accent" href={site.hero.ctaHref}>{copy(site.hero.cta, language)} <span aria-hidden="true">→</span></a>
          </div>
        </div>
      </div>
    </section>
  );
}

function Wave({ from, to }: { from: "paper" | "green"; to: "paper" | "green" }) {
  return <div className={`wave wave--${from}-to-${to}`} aria-hidden="true" />;
}

function GalleryDots({ count, active, language, onChange, onGoTo, itemName = "image" }: {
  count: number;
  active: number;
  language: Language;
  onChange: (direction: number) => void;
  onGoTo: (index: number) => void;
  itemName?: "image" | "dish" | "post";
}) {
  if (count < 2) return null;
  const names = { image: "Image", dish: "Dish", post: "Post" };
  const zhNames = { image: "張", dish: "道菜式", post: "則貼文" };
  return (
    <div className="gallery-dots" role="group" aria-label={language === "zh" ? "輪播控制" : "Carousel controls"}>
      <button type="button" className="gallery-dots__arrow" onClick={() => onChange(-1)}
        aria-label={language === "zh" ? `上一${zhNames[itemName]}` : `Previous ${itemName}`}><span aria-hidden="true">‹</span></button>
      <div className="gallery-dots__pages">
        {Array.from({ length: count }, (_, index) => (
          <button type="button" className={`gallery-dots__dot ${index === active ? "is-active" : ""}`}
            key={index} onClick={() => onGoTo(index)}
            aria-label={language === "zh" ? `第 ${index + 1} ${zhNames[itemName]}` : `${names[itemName]} ${index + 1}`}
            aria-current={index === active ? "true" : undefined} />
        ))}
      </div>
      <button type="button" className="gallery-dots__arrow" onClick={() => onChange(1)}
        aria-label={language === "zh" ? `下一${zhNames[itemName]}` : `Next ${itemName}`}><span aria-hidden="true">›</span></button>
    </div>
  );
}

function Philosophy({ site, language }: { site: SiteContent; language: Language }) {
  const images = site.philosophy.images?.length ? site.philosophy.images : defaultSite.philosophy.images;
  const { active, slots, change, goTo, swipe } = useCarousel(images.length, 1);

  return (
    <section id="philosophy" className="philosophy paper-section reveal" aria-labelledby="philosophy-title">
      <div className="page-shell philosophy__layout">
        <div className="philosophy__inner section-heading">
          <h2 id="philosophy-title" className="display-title">
            <span>{copy(site.philosophy.title, language)}</span>
          </h2>
          <div className="philosophy__body">
            {site.philosophy.paragraphs.map((paragraph) => (
              <p key={paragraph.en}>{copy(paragraph, language)}</p>
            ))}
          </div>
        </div>
        <div className="philosophy-gallery">
          <div className="philosophy-gallery__track" aria-live="polite" {...swipe}>
            {slots.map(({ key, index, position }) => {
              const image = images[index];
              return (
                <figure className={`philosophy-gallery__card ${carouselPositionClass(position)}`} key={key} aria-hidden={position !== 0}>
                  <img src={image.image} alt={position === 0 ? copy(image.alt, language) : ""} loading="lazy" />
                </figure>
              );
            })}
          </div>
          <GalleryDots count={images.length} active={active} language={language} onChange={change} onGoTo={goTo} />
        </div>
      </div>
    </section>
  );
}

function FlavourStory({ site, language }: { site: SiteContent; language: Language }) {
  const members = site.team.members.length ? site.team.members : defaultSite.team.members;
  const { active, slots, change, goTo, swipe } = useCarousel(members.length, 1);
  const current = members[active];

  return (
    <section id="team" className="flavour-story green-section" aria-labelledby="flavour-title">
      <div className="brand-field" aria-hidden="true" />
      <div className="page-shell flavour-story__grid reveal">
        <div className="flavour-story__copy">
          <h2 id="flavour-title">{copy(site.team.title, language)}</h2>
          <p className="flavour-story__intro">{copy(site.team.intro, language)}</p>
          <h3>{copy(current.name, language)}</h3>
          <p className="flavour-story__role">{copy(current.role, language)}</p>
          <p>{copy(current.bio, language)}</p>
        </div>
        <div className="flavour-gallery">
          <div className="flavour-stack" aria-live="polite" {...swipe}>
            {slots.map(({ key, index, position }) => {
              const member = members[index];
              return (
              <figure
                className={`flavour-card ${carouselPositionClass(position)}`}
                key={key}
                aria-hidden={position !== 0}
              >
                <img src={member.image} alt={position === 0 ? copy(member.alt, language) : ""} loading="lazy" />
              </figure>
              );
            })}
          </div>
          <GalleryDots count={members.length} active={active} language={language} onChange={change} onGoTo={goTo} />
        </div>
      </div>
    </section>
  );
}

function Menu({ site, language }: { site: SiteContent; language: Language }) {
  const items = site.menu.items.length ? site.menu.items : defaultSite.menu.items;
  const { active, slots, change, goTo, swipe } = useCarousel(items.length, 2);

  return (
    <section id="menu" className="menu-section paper-section" aria-labelledby="menu-title">
      <div className="page-shell reveal">
        <div className="menu-heading section-heading">
          <h2 id="menu-title" className="display-title">
            <span>{copy(site.menu.title, language)}</span>
          </h2>
          <p>{copy(site.menu.intro, language)}</p>
        </div>
        <div className="menu-carousel" aria-roledescription="carousel" aria-label={copy(site.interface.carouselLabel, language)}>
          <p className="sr-only">{copy(site.interface.carouselHint, language)}</p>
          <div className="menu-carousel__track" aria-live="polite" {...swipe}>
          {slots.map(({ key, index, position }) => {
            const item = items[index];
            return (
              <article className={`menu-item ${carouselPositionClass(position)}`} key={key} aria-hidden={position !== 0}>
                <div className="menu-item__image">
                  <img src={item.image} alt={position === 0 ? copy(item.alt, language) : ""} loading="lazy" />
                  <div className="menu-item__caption">
                    <h3>{copy(item.name, language)}</h3>
                    <p>{copy(item.description, language)}</p>
                  </div>
                </div>
              </article>
            );
          })}
          </div>
          <GalleryDots count={items.length} active={active} language={language} onChange={change} onGoTo={goTo} itemName="dish" />
        </div>
      </div>
    </section>
  );
}

function VisitHub({ site, language }: { site: SiteContent; language: Language }) {
  const [posts, setPosts] = useState<InstagramPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [allowEmbed, setAllowEmbed] = useState(true);
  const [mobile, setMobile] = useState(() => window.matchMedia("(max-width: 767px)").matches);
  const radius = mobile ? 1 : 2;
  const { active, slots, change, goTo, swipe } = useCarousel(posts.length, radius);
  const instagram = site.contact.social.find((item) => item.label === "Instagram");

  useEffect(() => {
    const query = window.matchMedia("(max-width: 767px)");
    const update = () => setMobile(query.matches);
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 20000);
    let mounted = true;
    fetch("/api/instagram", { signal: controller.signal, cache: "no-store" })
      .then(async (response) => {
        const feed = await response.json() as { posts?: InstagramPost[]; source?: string; embedFallbackAllowed?: boolean };
        if (!mounted) return;
        setAllowEmbed(feed.embedFallbackAllowed !== false);
        const showSelectedPosts = response.ok && Array.isArray(feed.posts) && feed.posts.length > 0;
        setPosts(showSelectedPosts && Array.isArray(feed.posts) ? feed.posts.slice(0, 12) : []);
      })
      .catch(() => { /* Show the official embed if the public feed is unavailable. */ })
      .finally(() => { window.clearTimeout(timeout); if (mounted) setLoading(false); });
    return () => { mounted = false; controller.abort(); window.clearTimeout(timeout); };
  }, []);

  return (
    <section id="visit" className="visit-hub green-section" aria-labelledby="visit-title">
      <div className="page-shell reveal">
        <div className="visit-hub__intro">
          <div>
            <h2 id="visit-title">{copy(site.contact.title, language)}</h2>
          </div>
        </div>
        <div className="visit-hub__columns">
          <div className="visit-hub__instagram" aria-labelledby="visit-updates-title">
            <div className="visit-hub__panel-head">
              <div>
                <h3 id="visit-updates-title">{language === "zh" ? "最新動態" : "Latest updates"}</h3>
              </div>
              {instagram && <a className="visit-hub__instagram-link" href={instagram.href} target="_blank" rel="noopener noreferrer"
                aria-label={language === "zh" ? "前往 Asura Instagram（新視窗）" : "Open Asura Instagram (new window)"}><SocialIcon label="Instagram" /></a>}
            </div>
            <div className="visit-hub__feed" aria-busy={loading}>
              {loading ? (
                <>
                  <p className="sr-only" role="status">{language === "zh" ? "正在載入最新動態" : "Loading updates"}</p>
                  <div className="social-gallery__track social-gallery__track--loading" aria-hidden="true">
                    {[0, 1, 2].map((key) => <div className="social-gallery__placeholder" key={key} />)}
                  </div>
                </>
              ) : posts.length ? (
                <div className="social-gallery__carousel">
                  <div className="social-gallery__track" aria-live="polite" {...swipe}>
                    {slots.map(({ key, index, position }) => {
                      const post = posts[index];
                      return <a className={`social-gallery__card ${carouselPositionClass(position)}`} key={key}
                        href={post.link} target="_blank" rel="noopener noreferrer"
                        aria-hidden={Math.abs(position) > radius} tabIndex={Math.abs(position) > radius ? -1 : 0}
                        aria-label={language === "zh" ? `在 Instagram 查看貼文 ${index + 1}（新視窗）` : `View Instagram post ${index + 1} (new window)`}>
                        <img src={post.image} alt="" loading={Math.abs(position) <= 2 ? "eager" : "lazy"} referrerPolicy="no-referrer"
                          onError={() => setPosts((current) => current.filter((item) => item.id !== post.id))} />
                      </a>;
                    })}
                  </div>
                  <GalleryDots count={posts.length} active={active} language={language} onChange={change} onGoTo={goTo} itemName="post" />
                </div>
              ) : allowEmbed ? (
                <div className="visit-hub__embed">
                  <iframe src="https://www.instagram.com/asura_thai/embed/" title={language === "zh" ? "Asura 官方 Instagram 個人頁" : "Asura official Instagram profile"}
                    loading="eager" referrerPolicy="strict-origin-when-cross-origin" />
                  <p>{language === "zh" ? "最新貼文暫時無法載入，請瀏覽官方 Instagram 頁面。" : "The latest posts are temporarily unavailable. Browse the official Instagram profile."}</p>
                </div>
              ) : (
                <p className="visit-hub__empty">{language === "zh" ? "目前沒有選擇在網站顯示的貼文。" : "No posts are currently selected for display here."}</p>
              )}
            </div>
          </div>
          <div className="visit-hub__locations" id="contact" aria-labelledby="visit-locations-title">
            <div className="visit-hub__panel-head">
              <div>
                <h3 id="visit-locations-title">{copy(site.reservations.title, language)}</h3>
              </div>
            </div>
            <div className="visit-hub__locations-scroll" role="region" aria-labelledby="visit-locations-title" tabIndex={0}>
              {site.contact.columns.map((column, index) => {
                const hasDirectContact = column.href.startsWith("tel:") || column.href.startsWith("https://wa.me/");
                const addressLines = column.lines.slice(hasDirectContact ? 1 : 0, -1);
                const address = addressLines.map((line) => line.zh).join(" ");
                const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
                const contactText = hasDirectContact && column.lines[0] ? copy(column.lines[0], language)
                  : language === "zh" ? "透過 Instagram 查詢" : "Enquire on Instagram";
                const hours = column.lines.at(-1);
                return <article className="visit-hub__location" key={column.title.en}>
                  <span className="visit-hub__location-number">{String(index + 1).padStart(2, "0")}</span>
                  <h4>{copy(column.title, language)}</h4>
                  {addressLines.length > 0 && <a className="visit-hub__address" href={mapUrl} target="_blank" rel="noopener noreferrer"
                    aria-label={language === "zh" ? `在 Google 地圖查看${copy(column.title, language)}（新視窗）` : `View ${copy(column.title, language)} on Google Maps (new window)`}>
                    {addressLines.map((line) => <span key={line.en}>{copy(line, language)}</span>)}
                    <strong>{language === "zh" ? "Google 地圖 ↗" : "Google Maps ↗"}</strong>
                  </a>}
                  {hours && <p className="visit-hub__hours">{copy(hours, language)}</p>}
                  <a className="visit-hub__contact-link" href={column.href} target={column.href.startsWith("https:") ? "_blank" : undefined}
                    rel={column.href.startsWith("https:") ? "noopener noreferrer" : undefined}>{contactText} <span aria-hidden="true">↗</span></a>
                </article>;
              })}
            </div>
            <p className="visit-hub__hours-note">{language === "zh" ? "營業時間或會調整，出發前請向分店確認。" : "Opening hours may change; please confirm with the location before visiting."}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function SocialIcon({ label }: { label: string }) {
    if (label.toLowerCase() === "instagram") {
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <rect x="3" y="3" width="18" height="18" rx="5" />
          <circle cx="12" cy="12" r="4.2" />
          <circle cx="17.4" cy="6.7" r="1" className="social-icon__dot" />
        </svg>
      );
    }
    if (label.toLowerCase() === "facebook") {
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path d="M13.8 21v-8h2.7l.4-3.1h-3.1V7.9c0-.9.3-1.5 1.6-1.5H17V3.6c-.7-.1-1.4-.2-2.1-.2-2.1 0-3.6 1.3-3.6 3.8v2.7H9V13h2.3v8h2.5Z" />
        </svg>
      );
    }
    return <span>{label}</span>;
}

function Footer({ site, language }: { site: SiteContent; language: Language }) {
  return (
    <footer className="site-footer green-section">
      <div className="brand-field" aria-hidden="true" />
      <div className="page-shell footer-inner reveal">
        <div className="footer-bottom">
          <a href="#top" aria-label={copy(site.brand.ariaLabel, language)}><BrandMark site={site} /></a>
          <div className="footer-bottom__links">
            {site.contact.social.map((item) => (
              <a
                key={item.label}
                href={item.href}
                className="social-link"
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${item.label}（${language === "zh" ? "新視窗" : "new window"}）`}
              >
                <SocialIcon label={item.label} />
              </a>
            ))}
          </div>
          <p>{copy(site.contact.legal, language)}</p>
          <nav className="footer-legal-links" aria-label={language === "zh" ? "法律頁面" : "Legal pages"}>
            <a href="/privacy">{language === "zh" ? "私隱政策" : "Privacy"}</a>
            <a href="/terms">{language === "zh" ? "使用條款" : "Terms"}</a>
          </nav>
        </div>
      </div>
    </footer>
  );
}

function LegalPage({ site, kind }: { site: SiteContent; kind: "privacy" | "terms" }) {
  const isPrivacy = kind === "privacy";
  const title = isPrivacy ? "私隱政策 | Privacy Policy" : "使用條款 | Terms of Use";
  useEffect(() => {
    document.title = `${title} | Asura Thai`;
    document.querySelector('meta[name="description"]')?.setAttribute("content", isPrivacy
      ? "Asura Thai 私隱政策，說明網站如何處理聯絡、分析及網站技術資料。"
      : "Asura Thai 使用條款，說明網站內容、外部連結及使用者責任。");
    document.querySelector('link[rel="canonical"]')?.setAttribute("href", `${site.seo.url}${kind}`);
  }, [isPrivacy, kind, site.seo.url, title]);
  return <div className="legal-page paper-section">
    <header className="legal-page__header page-shell">
      <a className="legal-page__brand" href="/"><BrandMark site={site} tone="dark" /><span>ASURA THAI</span></a>
      <h1>{title}</h1>
      <p>{isPrivacy ? "最後更新：2026 年 9 月 30 日" : "最後更新：2026 年 9 月 30 日"}</p>
    </header>
    <article className="legal-page__content page-shell">
      {isPrivacy ? <>
        <h2>我們收集的資料</h2><p>本網站主要提供餐廳資訊。當你主動透過電話、WhatsApp 或社交平台聯絡我們，相關平台可能按其自身政策處理你的資料。本網站不要求建立帳戶，也不在網站上收集付款資料。</p>
        <h2>網站技術資料</h2><p>網站可能處理必要的瀏覽器、裝置及伺服器記錄，以維持安全、效能及網站運作。語言偏好會儲存在你的瀏覽器本機儲存空間，你可以隨時清除。</p>
        <h2>外部連結</h2><p>地址、地圖、Instagram、Facebook 及 WhatsApp 連結會帶你前往第三方服務。第三方服務的資料處理受其私隱政策約束。</p>
        <h2>聯絡我們</h2><p>如對私隱有疑問，請透過網站列出的官方聯絡方式與 Asura Thai 聯絡。</p>
      </> : <>
        <h2>網站內容</h2><p>本網站提供 Asura Thai、Asuri 及 Phaya Asura 的餐廳、菜式、地址與聯絡資訊。營業時間、菜單及供應情況可能因分店或營運安排而變更，前往前請向分店確認。</p>
        <h2>外部服務</h2><p>本網站包含地圖、社交平台、WhatsApp 及其他第三方網站連結。使用這些服務時，請遵守相關服務的條款。</p>
        <h2>知識產權</h2><p>除另有註明外，本網站的文字、品牌標誌、相片及設計屬 Asura Thai 或其授權方所有。未經書面同意，不得複製或作商業用途。</p>
        <h2>責任限制</h2><p>我們會盡力維持資料準確及網站可用，但不保證所有資料在任何時間均完整、最新或不中斷。</p>
      </>}
    </article>
  </div>;
}

const locationSlugs = ["peninsula-centre", "airside-kai-tak", "hysan-place-causeway-bay", "phaya-asura-kai-tak"] as const;
const sectionSlugs = ["philosophy", "menu", "team", "visit"] as const;
function SectionPage({ site, slug }: { site: SiteContent; slug: string }) {
  const copyMap: Record<string, { zh: string; en: string; bodyZh: string; bodyEn: string }> = {
    philosophy: { zh: "品牌故事", en: "Our story", bodyZh: site.philosophy.paragraphs.map((p) => p.zh).join("\n\n"), bodyEn: site.philosophy.paragraphs.map((p) => p.en).join("\n\n") },
    menu: { zh: site.menu.title.zh, en: site.menu.title.en, bodyZh: site.menu.intro.zh, bodyEn: site.menu.intro.en },
    team: { zh: site.team.title.zh, en: site.team.title.en, bodyZh: site.team.intro.zh, bodyEn: site.team.intro.en },
    visit: { zh: site.contact.title.zh, en: site.contact.title.en, bodyZh: site.contact.legal.zh, bodyEn: site.contact.legal.en },
  };
  const section = copyMap[slug] ?? copyMap.philosophy;
  useEffect(() => { document.title = `${section.en} | Asura Thai`; document.querySelector('meta[name="description"]')?.setAttribute("content", section.bodyEn); document.querySelector('link[rel="canonical"]')?.setAttribute("href", `${site.seo.url}sections/${slug}`); }, [section, site.seo.url, slug]);
  return <div className="legal-page paper-section"><header className="legal-page__header page-shell"><a className="legal-page__brand" href="/"><BrandMark site={site} tone="dark" /><span>ASURA THAI</span></a><h1>{section.zh}<br /><small>{section.en}</small></h1><p>Asura Thai · Hong Kong</p></header><article className="legal-page__content page-shell"><h2>{section.zh}</h2>{section.bodyZh.split("\n\n").map((paragraph) => <p key={paragraph}>{paragraph}</p>)}<h2>{section.en}</h2>{section.bodyEn.split("\n\n").map((paragraph) => <p key={paragraph}>{paragraph}</p>)}<p><a href="/">返回首頁 · Back to Asura Thai</a></p></article></div>;
}
function LocationPage({ site, slug }: { site: SiteContent; slug: string }) {
  const index = locationSlugs.indexOf(slug as typeof locationSlugs[number]);
  const location = site.contact.columns[index] ?? site.contact.columns[0];
  useEffect(() => {
    document.title = `${location.title.en} | Asura Thai`;
    document.querySelector('meta[name="description"]')?.setAttribute("content", `${location.title.en}: ${location.lines.map((line) => line.en).join(". ")}`);
    document.querySelector('link[rel="canonical"]')?.setAttribute("href", `${site.seo.url}locations/${slug}`);
  }, [location, site.seo.url, slug]);
  return <div className="legal-page paper-section"><header className="legal-page__header page-shell"><a className="legal-page__brand" href="/"><BrandMark site={site} tone="dark" /><span>ASURA THAI</span></a><h1>{location.title.zh}<br /><small>{location.title.en}</small></h1><p>Asura Thai · Hong Kong</p></header><article className="legal-page__content page-shell"><h2>分店資訊</h2>{location.lines.map((line) => <p key={line.en}>{line.zh}<br />{line.en}</p>)}<p><a className="button button--accent" href={location.href}>聯絡分店 / Contact location</a></p><p><a href="/">返回首頁 · Back to Asura Thai</a></p></article></div>;
}

function SiteApp() {
  const [site, setSite] = useState<SiteContent>(defaultSite);
  const [language, setLanguage] = useState<Language>(() => {
    const stored = localStorage.getItem("asura-language");
    return stored === "en" ? "en" : "zh";
  });
  const [introVisible, setIntroVisible] = useState(true);
  const [introLeaving, setIntroLeaving] = useState(false);
  const [languageTransition, setLanguageTransition] = useState(false);

  useEffect(() => {
    const startedAt = performance.now();
    const finish = () => {
      const wait = Math.max(0, 900 - (performance.now() - startedAt));
      window.setTimeout(() => {
        setIntroLeaving(true);
        window.setTimeout(() => setIntroVisible(false), 1050);
      }, wait);
    };
    if (document.readyState === "complete") finish();
    else window.addEventListener("load", finish, { once: true });
    return () => window.removeEventListener("load", finish);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetch("/api/content", { signal: controller.signal, cache: "no-store", headers: { accept: "application/json" } })
      .then(async (response) => {
        if (!response.ok) return;
        const payload = await response.json() as { content?: SiteContent | null };
        if (payload.content && typeof payload.content === "object") setSite(upgradeSiteContent(payload.content));
      })
      .catch(() => {});
    return () => controller.abort();
  }, []);

  useEffect(() => {
    localStorage.setItem("asura-language", language);
    document.documentElement.lang = language === "zh" ? "zh-Hant-HK" : "en";
    document.title = copy(site.seo.title, language);
    document.querySelector('meta[name="description"]')?.setAttribute("content", copy(site.seo.description, language));
    document.querySelector('meta[property="og:title"]')?.setAttribute("content", copy(site.seo.title, language));
    document.querySelector('meta[property="og:description"]')?.setAttribute("content", copy(site.seo.description, language));
    document.querySelector('meta[property="og:image"]')?.setAttribute("content", new URL(site.seo.image, window.location.origin).href);
    document.querySelector('meta[property="og:url"]')?.setAttribute("content", site.seo.url);
    document.querySelector('link[rel="canonical"]')?.setAttribute("href", site.seo.url);
    const structured = document.getElementById("site-structured-data");
    if (structured) structured.textContent = serializeStructuredData(site);
  }, [language, site]);

  useEffect(() => {
    const elements = Array.from(document.querySelectorAll<HTMLElement>(".reveal"));
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      elements.forEach((element) => element.classList.add("is-visible"));
      return;
    }

    document.body.classList.add("reveal-ready");
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add("is-visible");
        observer.unobserve(entry.target);
      });
    }, { rootMargin: "0px 0px -12%", threshold: 0.08 });

    elements.forEach((element) => observer.observe(element));
    return () => {
      observer.disconnect();
      document.body.classList.remove("reveal-ready");
    };
  }, []);

  const siteStyle = {
    "--brand-pattern": `url("${site.brand.pattern}")`,
  } as CSSProperties;

  const changeLanguage = () => {
    if (languageTransition) return;
    setLanguageTransition(true);
    window.setTimeout(() => {
      setLanguage((value) => value === "zh" ? "en" : "zh");
      window.setTimeout(() => setLanguageTransition(false), 360);
    }, 180);
  };

  return (
    <>
    {introVisible && <IntroOverlay site={site} leaving={introLeaving} />}
    <div className={`site-frame ${languageTransition ? "is-language-changing" : ""}`} style={siteStyle}>
      <a className="skip-link" href="#main">{copy(site.interface.skip, language)}</a>
      <Header site={site} language={language} onLanguage={changeLanguage} />
      <main id="main">
        <Hero site={site} language={language} />
        <Philosophy site={site} language={language} />
        <Wave from="paper" to="green" />
        <FlavourStory site={site} language={language} />
        <Wave from="green" to="paper" />
        <Menu site={site} language={language} />
        <Wave from="paper" to="green" />
        <VisitHub site={site} language={language} />
      </main>
      <Footer site={site} language={language} />
    </div>
    </>
  );
}

function App() {
  const isCms = document.location.pathname.startsWith("/cms");
  if (isCms) return <CmsApp />;
  if (document.location.pathname === "/privacy" || document.location.pathname === "/terms") {
    return <LegalPage site={defaultSite} kind={document.location.pathname.slice(1) as "privacy" | "terms"} />;
  }
  if (document.location.pathname.startsWith("/locations/")) return <LocationPage site={defaultSite} slug={document.location.pathname.split("/")[2] ?? ""} />;
  if (document.location.pathname.startsWith("/sections/")) return <SectionPage site={defaultSite} slug={document.location.pathname.split("/")[2] ?? "philosophy"} />;
  return <SiteApp />;
}

export default App;
